import { ListIcon, Store } from "lucide-react"

import { FulfillmentType, OrderStatus, type Order } from "@/api/api-client"
import { FulfillmentDetails } from "@/components/orders/FulfillmentDetails"
import { OrderItemsTable } from "@/components/orders/OrderItemsTable"
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { formatCurrency } from "@/lib/productDisplay"
import { WarehouseDisplay } from "@/components/warehouse/WarehouseDisplay"

import type { BuyerShipmentLabels } from "./buyer-order-types"

interface BuyerShipmentAccordionProps {
  order: Order
  labels: BuyerShipmentLabels
  commonLabels: {
    quantity: string
    total: string
  }
  statusLabels: Partial<Record<string, string>>
  expandedPickupIds: Set<number>
  onTogglePickupAddress: (subOrderId: number) => void
}

export function BuyerShipmentAccordion({
  order,
  labels,
  commonLabels,
  statusLabels,
  expandedPickupIds,
  onTogglePickupAddress,
}: BuyerShipmentAccordionProps) {
  return (
    <Accordion type="single" collapsible className="w-full border-t border-border bg-muted/10">
      <AccordionItem value="shipments" className="border-none">
        <AccordionTrigger className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:no-underline">
          <span className="flex items-center gap-2">
            <ListIcon className="h-4 w-4" />
            {labels.shipmentBreakdown?.replace(
              "{count}",
              (order.subOrders?.length || 0).toString()
            ) || `DETAILED SHIPMENT BREAKDOWN (${order.subOrders?.length} SHIPMENTS)`}
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-6 px-6 pb-6 pt-0">
          {order.subOrders?.map((subOrder, index) => {
            const subStatus = subOrder.status as OrderStatus
            const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup

            return (
              <div
                key={subOrder.id}
                className="overflow-hidden rounded-lg border border-border/50 bg-background"
              >
                <div className="flex items-center justify-between border-b border-border/50 bg-muted/20 p-4">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-primary/50">0{index + 1}</span>
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        {isPickup
                          ? labels.pickupShipment
                          : labels.originShipment || "ORIGIN SHIPMENT"}
                      </span>
                      <span className="text-xs font-bold uppercase tracking-widest">
                        {labels.reference}: {subOrder.id}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isPickup && (
                      <Badge
                        variant="outline"
                        className="border-cyan-200 bg-cyan-50 text-xs font-bold uppercase tracking-widest text-cyan-700"
                      >
                        <Store className="mr-1 h-3 w-3" /> {labels.pickupBadge}
                      </Badge>
                    )}
                    <OrderStatusBadge
                      status={subStatus}
                      labels={statusLabels}
                      className="tracking-widest"
                    />
                  </div>
                </div>

                <OrderItemsTable
                  items={subOrder.items}
                  labels={{
                    product: labels.product,
                    sku: labels.sku,
                    quantity: commonLabels.quantity,
                    unitPrice: labels.unitPrice,
                    total: commonLabels.total,
                    notAvailable: labels.notAvailable,
                  }}
                />

                <div className="space-y-3 border-t border-border/50 bg-muted/10 px-4 py-3">
                  {isPickup ? (
                    <WarehouseDisplay
                      warehouse={subOrder.pickupWarehouse}
                      fallbackName={labels.warehouseFallback.replace(
                        "{id}",
                        String(subOrder.pickupWarehouseId ?? labels.notAvailable)
                      )}
                      isExpanded={subOrder.id !== undefined && expandedPickupIds.has(subOrder.id)}
                      onToggle={() =>
                        subOrder.id !== undefined && onTogglePickupAddress(subOrder.id)
                      }
                    />
                  ) : (
                    <FulfillmentDetails compact recipientName={order.receiverName} />
                  )}

                  {subOrder.shippingCost != null && subOrder.shippingCost > 0 && (
                    <div className="space-y-1 border-t border-border/30 pt-2">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{labels.productSubtotal}</span>
                        <span className="font-bold text-foreground">
                          {formatCurrency((subOrder.totalAmount ?? 0) - subOrder.shippingCost)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{labels.shippingCost}</span>
                        <span className="font-bold text-foreground">
                          {formatCurrency(subOrder.shippingCost)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-border/30 pt-1 text-xs font-black uppercase tracking-widest">
                        <span>{labels.total}</span>
                        <span className="text-foreground">
                          {formatCurrency(subOrder.totalAmount ?? 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
